import type React from "react";
import type { FieldSchema } from "./editorSchema";
import {
  getAllArtists,
  getAllDays,
  getAllStages,
  getAllPerformances,
} from "../../services/dataService";

interface EntityFormProps {
  // biome-ignore lint/suspicious/noExplicitAny: entity data can have any key-value structure
  data: any;
  schema: FieldSchema[];
  // biome-ignore lint/suspicious/noExplicitAny: callback receives the updated entity structure
  onChange: (updatedData: any) => void;
}

export const EntityForm: React.FC<EntityFormProps> = ({ data, schema, onChange }) => {
  // Utility to fetch dynamic dropdown choices
  const getDropdownOptions = (source: NonNullable<FieldSchema["optionsSource"]>) => {
    const list: { value: string; label: string }[] = [];

    if (source.collection === "artists") {
      for (const item of getAllArtists()) {
        list.push({
          value: item.id,
          label: (item[source.labelField as keyof typeof item] as string) || item.id,
        });
      }
    } else if (source.collection === "days") {
      for (const item of getAllDays()) {
        list.push({
          value: item.id,
          label: (item[source.labelField as keyof typeof item] as string) || item.id,
        });
      }
    } else if (source.collection === "stages") {
      for (const item of getAllStages()) {
        list.push({
          value: item.id,
          label: (item[source.labelField as keyof typeof item] as string) || item.id,
        });
      }
    } else if (source.collection === "performances") {
      for (const item of getAllPerformances()) {
        list.push({
          value: item.id,
          label: (item[source.labelField as keyof typeof item] as string) || item.id,
        });
      }
    }

    return list.sort((a, b) => a.label.localeCompare(b.label));
  };

  // Generic value updater for paths (e.g. ["themeColors", "primary"])
  // biome-ignore lint/suspicious/noExplicitAny: handles nested object re-assignment
  const handleFieldChange = (keyPath: string[], value: any) => {
    const updated = { ...data };
    let current = updated;
    for (let i = 0; i < keyPath.length - 1; i++) {
      const part = keyPath[i];
      current[part] = { ...current[part] };
      current = current[part];
    }
    const lastKey = keyPath[keyPath.length - 1];

    if (value === "") {
      current[lastKey] = null;
    } else {
      current[lastKey] = value;
    }
    onChange(updated);
  };

  // Helper to extract nested values safely
  const getValueAtPath = (keyPath: string[]) => {
    let current = data;
    for (const part of keyPath) {
      if (current === null || current === undefined) return "";
      current = current[part];
    }
    return current ?? "";
  };

  // Recursive renderer for fields
  const renderField = (field: FieldSchema, parentPath: string[] = []) => {
    const keyPath = [...parentPath, field.key];
    const pathStr = keyPath.join(".");
    const currentVal = getValueAtPath(keyPath);

    switch (field.type) {
      case "textarea":
        return (
          <div key={pathStr} className="space-y-1">
            <label htmlFor={pathStr} className="block text-sm font-semibold text-text-muted">
              {field.label} {field.required && <span className="text-pink-500">*</span>}
            </label>
            <textarea
              id={pathStr}
              value={String(currentVal)}
              onChange={(e) => handleFieldChange(keyPath, e.target.value)}
              className="w-full px-3 py-2 border border-border bg-background-accent text-text rounded-lg shadow-inner focus:outline-none focus:ring-2 focus:ring-main/40 focus:border-main transition duration-150 ease-in-out font-sans text-sm resize-y"
              rows={3}
              required={field.required}
            />
          </div>
        );

      case "number":
        return (
          <div key={pathStr} className="space-y-1">
            <label htmlFor={pathStr} className="block text-sm font-semibold text-text-muted">
              {field.label} {field.required && <span className="text-pink-500">*</span>}
            </label>
            <input
              id={pathStr}
              type="number"
              value={currentVal === null || currentVal === undefined ? "" : Number(currentVal)}
              onChange={(e) =>
                handleFieldChange(keyPath, e.target.value === "" ? null : Number(e.target.value))
              }
              className="w-full px-3 py-2 border border-border bg-background-accent text-text rounded-lg shadow-inner focus:outline-none focus:ring-2 focus:ring-main/40 focus:border-main transition duration-150 ease-in-out font-sans text-sm"
              required={field.required}
            />
          </div>
        );

      case "boolean":
        return (
          <div key={pathStr} className="flex items-center space-x-3 py-2">
            <input
              id={pathStr}
              type="checkbox"
              checked={!!currentVal}
              onChange={(e) => handleFieldChange(keyPath, e.target.checked)}
              className="h-4 w-4 text-main focus:ring-main/40 border-border bg-background-accent rounded cursor-pointer"
            />
            <label
              htmlFor={pathStr}
              className="text-sm font-semibold text-text select-none cursor-pointer hover:text-white transition-colors"
            >
              {field.label}
            </label>
          </div>
        );

      case "select": {
        const options = field.optionsSource ? getDropdownOptions(field.optionsSource) : [];
        return (
          <div key={pathStr} className="space-y-1">
            <label htmlFor={pathStr} className="block text-sm font-semibold text-text-muted">
              {field.label} {field.required && <span className="text-pink-500">*</span>}
            </label>
            <select
              id={pathStr}
              value={String(currentVal)}
              onChange={(e) => handleFieldChange(keyPath, e.target.value)}
              className="w-full px-3 py-2 border border-border bg-background-accent text-text rounded-lg focus:outline-none focus:ring-2 focus:ring-main/40 focus:border-main transition duration-150 ease-in-out font-sans text-sm cursor-pointer"
              required={field.required}
            >
              <option value="" className="bg-background text-text">
                -- Select {field.label} --
              </option>
              {options.map((opt) => (
                <option key={opt.value} value={opt.value} className="bg-background text-text">
                  {opt.label} ({opt.value})
                </option>
              ))}
            </select>
          </div>
        );
      }

      case "object":
        return (
          <div
            key={pathStr}
            className="border border-border rounded-xl p-4 bg-background/40 space-y-3 col-span-full"
          >
            <h3 className="text-sm font-bold text-text border-b border-border pb-2 mb-2">
              {field.label}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {field.properties?.map((subField) => renderField(subField, keyPath))}
            </div>
          </div>
        );

      default:
        return (
          <div key={pathStr} className="space-y-1">
            <label htmlFor={pathStr} className="block text-sm font-semibold text-text-muted">
              {field.label} {field.required && <span className="text-pink-500">*</span>}
            </label>
            <input
              id={pathStr}
              type="text"
              value={String(currentVal)}
              onChange={(e) => handleFieldChange(keyPath, e.target.value)}
              className="w-full px-3 py-2 border border-border bg-background-accent text-text rounded-lg shadow-inner focus:outline-none focus:ring-2 focus:ring-main/40 focus:border-main transition duration-150 ease-in-out font-sans text-sm"
              required={field.required}
            />
          </div>
        );
    }
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-card border border-border p-6 rounded-xl shadow-lg">
        {schema.map((field) => renderField(field))}
      </div>
    </div>
  );
};
